import React from 'react';
import { Alert, Box, MenuItem, TextField, Theme, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette, useQlfTranslation } from '@qualifio/ui-commons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faLightbulbOn } from '@fortawesome/pro-solid-svg-icons/faLightbulbOn';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { CustomLink } from '../../../shared/CustomLink';
import { LoginFormValue, LoginMethod } from '../../../../pages/program-login';
import { RedirectUrisWhitelist } from '../../../auth/components/redirectUrisWhitelist';
import { useAccounts } from '../../../accounts/AccountsContext';
import { useFeatures } from '../../../features/FeaturesContext';
import { useProgram } from '../../../loyalty/ProgramContext';
import { useProgramLoginPage } from '../../ProgramLoginPageProvider';

const styles = ({ spacing }: Theme) => {
  return {
    title: {
      fontWeight: 'bold',
      marginTop: `${spacing(3)} !important`,
      marginBottom: `${spacing(2)} !important`,
    },
    errorIcon: {
      marginLeft: spacing(0.5),
    },
    helper: {
      marginTop: spacing(2),
      padding: spacing(2),
      '& a': {
        textDecoration: 'none !important',
        color: colorPalette.blue.main,
      },
    },
    loginPageForm: {
      marginBottom: '30px',
    },
    methodSelector: {},
    optionSelector: {
      marginTop: '20px',
    },
  };
};
const useStyles = makeStyles(styles);

export function LoginPageForm(): React.ReactElement {
  const { features } = useFeatures();
  const { write } = features[Feature.LOGIN_PAGE];
  const { loginPages, integrations } = useProgramLoginPage();
  const { t } = useTranslation();
  const { t: htmlT } = useQlfTranslation('translation');
  const { currentAccount } = useAccounts();
  const { program } = useProgram();
  const classes = useStyles();

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<LoginFormValue>();

  const selectedMethod = watch('loginMethod');

  const optionSelector = () => {
    if (selectedMethod === LoginMethod.PAGE) {
      if (loginPages.data?.length) {
        return (
          <Box sx={theme => ({ marginTop: theme.spacing(1) })}>
            <Typography sx={{ marginBottom: 1 }} variant="body1">
              {t('access-rule.login.page.description')}
            </Typography>
            <Controller
              control={control}
              name="pageId"
              shouldUnregister={false}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <>
                  <TextField
                    disabled={!write}
                    {...field}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    select
                    placeholder={t('access-rule.login.page.dropdown.placeholder')}
                  >
                    {loginPages.data?.map(page => (
                      <MenuItem key={page.id} value={page.id}>
                        {page.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}
            />
          </Box>
        );
      } else {
        return (
          <Alert
            severity="warning"
            className={classes.helper}
            icon={<FontAwesomeIcon icon={faLightbulbOn} />}
          >
            {htmlT('access-rules.login.page.helper1')}
            <CustomLink to={`/accounts/${currentAccount.id}/login-pages`} rel="noreferrer">
              {t('access-rules.login.page.helper2')}
            </CustomLink>
          </Alert>
        );
      }
    }
    if (selectedMethod === LoginMethod.INTEGRATION) {
      if (integrations.data?.length) {
        return (
          <Box sx={theme => ({ marginTop: theme.spacing(1) })}>
            <Typography sx={{ marginBottom: 1 }} variant="body1">
              {t('access-rule.login.integration.description')}
            </Typography>
            <Controller
              control={control}
              name="integrationId"
              shouldUnregister={false}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <>
                  <TextField
                    disabled={!write}
                    {...field}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    select
                    placeholder={t('access-rule.login.integration.dropdown.placeholder')}
                  >
                    {integrations.data?.map(integration => (
                      <MenuItem key={integration.id} value={integration.id}>
                        {integration.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}
            />
          </Box>
        );
      } else {
        return (
          <Alert
            severity="warning"
            className={classes.helper}
            icon={<FontAwesomeIcon icon={faLightbulbOn} />}
          >
            {htmlT('access-rules.login.integration.helper1')}
            <CustomLink
              to={`/accounts/${currentAccount.id}/integrations/integrations`}
              rel="noreferrer"
            >
              {t('access-rules.login.integration.helper2')}
            </CustomLink>
          </Alert>
        );
      }
    }
  };

  return (
    <div className={classes.loginPageForm}>
      <Typography variant="h3" className={classes.title}>
        {t('access-rules.login.page.title')}
        {!!errors.pageId && (
          <FontAwesomeIcon className={classes.errorIcon} color="red" icon={faExclamationCircle} />
        )}
      </Typography>
      <Typography sx={{ marginBottom: 1 }} variant="body1">
        {t('access-rule.login.description')}
      </Typography>
      <div className={classes.methodSelector}>
        <Controller
          control={control}
          name="loginMethod"
          shouldUnregister={false}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <>
              <TextField
                disabled={!write}
                {...field}
                inputRef={ref}
                error={!!error}
                helperText={error?.message}
                select
              >
                <MenuItem key={LoginMethod.PAGE} value={LoginMethod.PAGE}>
                  {t('access-rule.login.dropdown.login-page')}
                </MenuItem>
                <MenuItem key={LoginMethod.INTEGRATION} value={LoginMethod.INTEGRATION}>
                  {t('access-rule.login.dropdown.integration')}
                </MenuItem>
              </TextField>
            </>
          )}
        />
      </div>
      {optionSelector()}
      <RedirectUrisWhitelist />
    </div>
  );
}
